// Master Apps · tela de erro das páginas de dados.
//
// Toda página que carrega dados por query renderiza este componente quando a
// query falha sem dado em cache. Sem isso, o padrão `isLoading || !dados`
// deixa o spinner girando para sempre: com `retry: 1` no QueryClient, depois da
// segunda falha o `isLoading` volta a false e o `data` continua undefined.
//
// O chrome (Sidebar + TopBar) é montado aqui a partir dos primitivos, para o
// usuário nunca ficar preso numa tela de erro sem navegação.
import type { ReactNode } from 'react';
import { Navigate, useNavigate } from 'react-router';
import {
  IconAlert,
  IconClock,
  IconGrid,
  IconLock,
  IconRefresh,
  IconWifi,
} from '@/components/icons';
import { Sidebar, TopBar } from '@/components/primitives';
import { ApiError, useMe } from '@/hooks/use-applications';
import { byViewport, useViewport, type Viewport } from '@/hooks/use-viewport';
import { fontMono, fontSans, makePalette, type Palette } from '@/lib/theme';
import { useScreenProps } from '@/store/theme';

export type ScreenErrorKind =
  | 'permission'
  | 'rateLimit'
  | 'network'
  | 'server'
  | 'notFound'
  | 'generic';

// Códigos de acesso da API que NÃO são erro de tela: o usuário precisa terminar
// o fluxo de aprovação/onboarding. O AccessGate já roteia isso na entrada, mas
// uma query pode falhar depois (token expirado, status alterado por um admin) e
// aí o redirect evita o spinner e o cartão de erro inútil.
const REDIRECT_BY_CODE: Record<string, string> = {
  PENDING_APPROVAL: '/aguardando',
  ACCOUNT_REJECTED: '/aguardando',
  NEEDS_ONBOARDING: '/primeiro-acesso',
};

/** Rota para onde o erro manda o usuário, ou null quando é erro de tela mesmo. */
export function accessRedirectFor(error: unknown): string | null {
  if (!(error instanceof ApiError) || !error.code) return null;
  return REDIRECT_BY_CODE[error.code] ?? null;
}

/** Classifica o erro para escolher ícone, título e texto de ajuda. */
export function screenErrorKind(error: unknown): ScreenErrorKind {
  if (error instanceof ApiError) {
    if (error.status === 429 || error.code === 'RATE_LIMIT') return 'rateLimit';
    if (error.status === 401 || error.status === 403) return 'permission';
    if (error.status === 404) return 'notFound';
    if (error.status >= 500 || error.status === 0) return 'server';
    return 'generic';
  }
  const message = error instanceof Error ? error.message.toLowerCase() : '';
  if (/fetch|network|networkerror|failed to|load failed|conex|offline/.test(message)) {
    return 'network';
  }
  return 'generic';
}

const COPY: Record<ScreenErrorKind, { heading: string; description: string }> = {
  permission: {
    heading: 'Você não tem permissão para ver isto',
    description:
      'Seu login continua válido, mas seu usuário não tem permissão para acessar estes dados. Se você precisa desse acesso para o seu trabalho, fale com a TI.',
  },
  rateLimit: {
    heading: 'Muitas tentativas em pouco tempo',
    description:
      'O servidor bloqueou temporariamente novas consultas do seu usuário. Aguarde alguns instantes antes de tentar de novo.',
  },
  network: {
    heading: 'Sem conexão com o servidor',
    description:
      'Não foi possível falar com a API do Master Apps. Verifique sua conexão de rede e tente novamente.',
  },
  server: {
    heading: 'O servidor não conseguiu responder',
    description:
      'Ocorreu uma falha no servidor ao buscar os dados desta tela. Tente novamente em alguns instantes.',
  },
  notFound: {
    heading: 'Não encontramos estes dados',
    description:
      'O registro pode ter sido removido ou o link que você abriu está desatualizado. Volte ao catálogo e tente de novo pela navegação.',
  },
  generic: {
    heading: 'Falha ao carregar os dados',
    description:
      'Não foi possível carregar o conteúdo desta tela. Verifique sua conexão e tente novamente.',
  },
};

function iconFor(kind: ScreenErrorKind): ReactNode {
  if (kind === 'permission') return <IconLock size={20} />;
  if (kind === 'rateLimit') return <IconClock size={20} />;
  if (kind === 'network') return <IconWifi size={20} />;
  return <IconAlert size={20} />;
}

// Linha técnica do cartão: status, code e mensagem crua da API. Serve para o
// usuário mandar print para a TI sem precisar abrir o console.
/** Mensagem crua de um erro de tipo desconhecido (Error, string ou outra coisa). */
function rawMessage(error: unknown): string | undefined {
  if (error instanceof Error) return error.message;
  if (typeof error === 'string') return error;
  return undefined;
}

function techDetail(error: unknown): string {
  const parts: string[] = [];
  if (error instanceof ApiError) {
    parts.push(`HTTP ${error.status}`);
    if (error.code) parts.push(error.code);
  }
  const message = rawMessage(error);
  if (message) parts.push(message);
  return parts.join(' · ');
}

/**
 * Título e texto finais do cartão: o override explícito vence; sem override,
 * `rateLimit` com `retryAfterSec` ganha o texto com a contagem em segundos.
 */
function deriveCopy(
  kind: ScreenErrorKind,
  error: unknown,
  heading: string | undefined,
  description: string | undefined,
): { heading: string; description: string } {
  const copy = COPY[kind];
  const retryAfter = error instanceof ApiError ? error.details?.retryAfterSec : undefined;
  const timed =
    kind === 'rateLimit' && retryAfter
      ? `O servidor bloqueou temporariamente novas consultas do seu usuário. Aguarde ${retryAfter} segundos e tente de novo.`
      : copy.description;
  return { heading: heading ?? copy.heading, description: description ?? timed };
}

/**
 * Caixa comum dos botões do cartão. No toque eles crescem para 44px e passam a
 * ocupar a linha inteira: em 320px "Voltar ao catálogo" + "Tentar novamente" já
 * quebravam de linha (o `flexWrap` do rodapé), e sobravam dois retângulos
 * estreitos de 34px em vez de um alvo por linha.
 */
function buttonBox(vp: Viewport) {
  return {
    height: byViewport(vp, { mobile: 44, desktop: 34 }),
    flex: byViewport(vp, { mobile: '1 1 100%', desktop: undefined }),
    justifyContent: 'center',
  } as const;
}

// Os dois estilos de botão do cartão, como fábricas puras.
const primaryStyle = (accent: string, vp: Viewport) =>
  ({
    ...buttonBox(vp),
    padding: '0 16px',
    background: accent,
    color: '#fff',
    border: 'none',
    borderRadius: 7,
    fontSize: 12.5,
    fontWeight: 600,
    fontFamily: fontSans,
    display: 'inline-flex',
    alignItems: 'center',
    gap: 7,
    textDecoration: 'none',
  }) as const;

const ghostStyle = (p: Palette, vp: Viewport) =>
  ({
    ...buttonBox(vp),
    padding: '0 14px',
    background: 'transparent',
    color: p.textMuted,
    border: `1px solid ${p.borderStrong}`,
    borderRadius: 7,
    fontSize: 12.5,
    fontWeight: 600,
    fontFamily: fontSans,
    display: 'inline-flex',
    alignItems: 'center',
    gap: 7,
    textDecoration: 'none',
  }) as const;

interface RetryButtonProps {
  onRetry: () => void;
  retrying: boolean;
  /** Com botão de voltar ao catálogo o "tentar novamente" vira o secundário. */
  ghost: boolean;
  accent: string;
  p: Palette;
}

function RetryButton({ onRetry, retrying, ghost, accent, p }: Readonly<RetryButtonProps>) {
  const vp = useViewport();
  return (
    <button
      type="button"
      onClick={onRetry}
      disabled={retrying}
      style={{
        ...(ghost ? ghostStyle(p, vp) : primaryStyle(accent, vp)),
        cursor: retrying ? 'default' : 'pointer',
        opacity: retrying ? 0.72 : 1,
      }}
    >
      {retrying ? (
        <span
          style={{
            width: 12,
            height: 12,
            borderRadius: '50%',
            border: `2px solid ${ghost ? p.border : 'rgba(255,255,255,0.4)'}`,
            borderTopColor: ghost ? p.textMuted : '#fff',
            animation: 'spin 0.8s linear infinite',
            flexShrink: 0,
          }}
        />
      ) : (
        <IconRefresh size={13} />
      )}
      {retrying ? 'Tentando novamente…' : 'Tentar novamente'}
    </button>
  );
}

interface ErrorCardProps {
  kind: ScreenErrorKind;
  heading: string;
  description: string;
  /** Linha técnica (HTTP/code/mensagem); vazia esconde o bloco monoespaçado. */
  tech: string;
  dark: boolean;
  p: Palette;
  actions: ReactNode;
}

function ErrorCard({
  kind,
  heading,
  description,
  tech,
  dark,
  p,
  actions,
}: Readonly<ErrorCardProps>) {
  const vp = useViewport();
  return (
    <div
      style={{
        width: '100%',
        // `min(440px, 100%)` em vez de 440 fixo: o cartão nunca passa da largura
        // útil nem que o container de fora perca o padding.
        maxWidth: 'min(440px, 100%)',
        background: p.surface,
        border: `1px solid ${p.border}`,
        borderRadius: 12,
        padding: byViewport(vp, { mobile: 18, desktop: 28 }),
        textAlign: 'center',
        boxShadow: dark ? '0 18px 48px rgba(0,0,0,0.45)' : '0 18px 48px rgba(0,0,0,0.06)',
      }}
    >
      <div
        style={{
          width: byViewport(vp, { mobile: 40, desktop: 44 }),
          height: byViewport(vp, { mobile: 40, desktop: 44 }),
          borderRadius: 10,
          margin: '0 auto 14px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          background: dark ? '#dc262622' : '#fee2e2',
          color: dark ? '#fca5a5' : '#b91c1c',
        }}
      >
        {iconFor(kind)}
      </div>
      <div
        style={{
          fontSize: byViewport(vp, { mobile: 15, desktop: 15.5 }),
          fontWeight: 600,
          color: p.text,
          letterSpacing: '-0.01em',
          marginBottom: 8,
        }}
      >
        {heading}
      </div>
      {/* 12.5px de explicação é pequeno num celular: 13.5 no compacto. */}
      <div
        style={{
          fontSize: byViewport(vp, { mobile: 13.5, desktop: 12.5 }),
          color: p.muted,
          lineHeight: 1.6,
          marginBottom: 18,
        }}
      >
        {description}
      </div>
      {tech && (
        <div
          style={{
            background: p.surface2,
            border: `1px solid ${p.border}`,
            borderRadius: 8,
            padding: '8px 10px',
            marginBottom: 18,
            fontFamily: fontMono,
            fontSize: byViewport(vp, { mobile: 11.5, desktop: 10.5 }),
            lineHeight: 1.5,
            color: p.muted,
            textAlign: 'left',
            wordBreak: 'break-word',
          }}
        >
          {tech}
        </div>
      )}
      <div style={{ display: 'flex', gap: 8, justifyContent: 'center', flexWrap: 'wrap' }}>
        {actions}
      </div>
    </div>
  );
}

export interface ScreenErrorProps {
  /** Erro devolvido pela query (ApiError na maioria dos casos). */
  error: unknown;
  /** Normalmente `() => void refetch()` da query que falhou. */
  onRetry?: () => void;
  /** `isFetching`/`isRefetching` da query: liga o estado de carregando do botão. */
  retrying?: boolean;
  /** Item destacado na Sidebar (mesmos ids da navegação). */
  active?: string;
  /** Cabeçalho da TopBar, para o usuário reconhecer a tela que falhou. */
  title?: string;
  subtitle?: string;
  breadcrumb?: string[];
  /** Papel do usuário. Por padrão vem do perfil em cache (/api/me). */
  role?: 'admin' | 'collab';
  /** false monta o cartão em tela cheia, sem Sidebar/TopBar. */
  chrome?: boolean;
  /** Sobrescreve o título do cartão derivado do erro. */
  heading?: string;
  /** Sobrescreve o texto do cartão derivado do erro. */
  description?: string;
}

export function ScreenError({
  error,
  onRetry,
  retrying = false,
  active = 'catalog',
  title = 'Master Apps',
  subtitle,
  breadcrumb,
  role,
  chrome = true,
  heading,
  description,
}: Readonly<ScreenErrorProps>) {
  const sp = useScreenProps();
  const navigate = useNavigate();
  const { data: me } = useMe();
  const vp = useViewport();
  const p = makePalette(sp.dark);

  // Depois dos hooks: pendência de aprovação/onboarding vira rota, não cartão.
  const redirect = accessRedirectFor(error);
  if (redirect) return <Navigate to={redirect} replace />;

  const kind = screenErrorKind(error);
  const copy = deriveCopy(kind, error, heading, description);
  const showBack = kind === 'permission' || kind === 'notFound';
  const effectiveRole = role ?? (me?.role === 'admin' ? 'admin' : 'collab');

  const card = (
    <ErrorCard
      kind={kind}
      heading={copy.heading}
      description={copy.description}
      tech={techDetail(error)}
      dark={sp.dark}
      p={p}
      actions={
        <>
          {showBack && (
            <button
              type="button"
              onClick={() => navigate('/catalog')}
              style={{ ...primaryStyle(sp.accent, vp), cursor: 'pointer' }}
            >
              <IconGrid size={13} />
              Voltar ao catálogo
            </button>
          )}
          {onRetry && (
            <RetryButton
              onRetry={onRetry}
              retrying={retrying}
              ghost={showBack}
              accent={sp.accent}
              p={p}
            />
          )}
        </>
      }
    />
  );

  if (!chrome) {
    return (
      <div
        style={{
          width: '100%',
          // Tela cheia sem chrome: no compacto a altura vira mínima e a rolagem
          // volta. Com `height: 100vh` + `overflow: hidden`, um detalhe técnico
          // comprido (stack de fetch) deixava o botão "Tentar novamente"
          // inalcançável abaixo da dobra. `dvh` porque a barra de endereço do
          // celular come parte de `vh`.
          // `--app-vh` (de `globals.css`) em vez de `100dvh`/`100vh` crus: a
          // variável já traz o fallback para navegador sem `dvh`. No compacto a
          // altura é MÍNIMA (com `height: auto`) para o cartão poder crescer.
          height: byViewport(vp, { mobile: 'auto', desktop: 'var(--app-vh)' }),
          minHeight: byViewport(vp, { mobile: 'var(--app-vh)', desktop: undefined }),
          display: 'flex',
          alignItems: byViewport(vp, { mobile: 'flex-start', desktop: 'center' }),
          justifyContent: 'center',
          padding: byViewport(vp, { mobile: 14, desktop: 24 }),
          background: p.bg,
          color: p.text,
          fontFamily: fontSans,
          overflow: byViewport(vp, { mobile: 'visible', desktop: 'hidden' }),
        }}
      >
        {card}
      </div>
    );
  }

  return (
    <div
      style={{
        width: '100%',
        height: 'var(--app-vh)',
        fontFamily: fontSans,
        color: p.text,
        background: p.bg,
        display: 'flex',
        overflow: 'hidden',
      }}
    >
      {/* A Sidebar já sabe se recolher (gaveta) abaixo de 1024px — aqui ela
          continua entrando para o usuário nunca ficar sem navegação no erro. */}
      <Sidebar active={active} dark={sp.dark} accent={sp.accent} role={effectiveRole} />
      <div style={{ flex: 1, display: 'flex', flexDirection: 'column', minWidth: 0 }}>
        <TopBar dark={sp.dark} title={title} subtitle={subtitle} breadcrumb={breadcrumb} />
        <div
          style={{
            flex: 1,
            padding: byViewport(vp, { mobile: 14, desktop: 24 }),
            display: 'flex',
            // Mesmo motivo do caso sem chrome: com o cartão mais alto que a
            // área útil, `center` corta o topo e não há como rolar até ele.
            alignItems: byViewport(vp, { mobile: 'flex-start', desktop: 'center' }),
            justifyContent: 'center',
            overflowY: byViewport(vp, { mobile: 'auto', desktop: 'visible' }),
            minHeight: 0,
          }}
        >
          {card}
        </div>
      </div>
    </div>
  );
}
